import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle } from 'lucide-react';

interface PAMSSACPillProps {
  P: number;  // Placement
  A1: number; // Access
  M: number;  // Motivation
  S1: number; // Suitability
  S2: number; // Susceptibility
  A2: number; // Accessibility
  C: number;  // Commonalities
  total: number;
  weakestTwo?: Array<{ dimension: string; score: number; why?: string; evidence?: string[] }>;
  className?: string;
}

export function PAMSSACPill({ P, A1, M, S1, S2, A2, C, total, weakestTwo, className = '' }: PAMSSACPillProps) {
  const dimensions = [
    { key: 'P', value: P, label: 'Placement', weight: 18 },
    { key: 'A1', value: A1, label: 'Access', weight: 15 },
    { key: 'M', value: M, label: 'Motivation', weight: 20 },
    { key: 'S1', value: S1, label: 'Suitability', weight: 15 },
    { key: 'S2', value: S2, label: 'Susceptibility', weight: 12 },
    { key: 'A2', value: A2, label: 'Accessibility', weight: 10 },
    { key: 'C', value: C, label: 'Commonalities', weight: 10 }
  ];

  // Use darker theme colors with tint
  const getSegmentColor = (value: number) => {
    if (value >= 75) return 'bg-[#008080]'; // Teal (theme color)
    if (value >= 50) return 'bg-[#D4AF37]'; // Gold (theme color)
    return 'bg-[#8B4513]'; // Dark brown (theme darker tone)
  };

  const getSegmentHoverColor = (value: number) => {
    if (value >= 75) return 'bg-[#005f5f]'; // Darker teal
    if (value >= 50) return 'bg-[#B8960F]'; // Darker gold
    return 'bg-[#6B3410]'; // Even darker brown
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-2 ${className}`}>
            {/* Stacked bar - 7 segments */}
            <div className="flex h-5 w-32 rounded-full overflow-hidden border border-border/50 shadow-sm">
              {dimensions.map((dim, i) => (
                <TooltipProvider key={dim.key} delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="transition-all cursor-pointer"
                        style={{ 
                          width: `${dim.weight}%`,
                          backgroundColor: dim.value >= 75 ? '#008080' : dim.value >= 50 ? '#D4AF37' : '#8B4513'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = dim.value >= 75 ? '#005f5f' : dim.value >= 50 ? '#B8960F' : '#6B3410';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = dim.value >= 75 ? '#008080' : dim.value >= 50 ? '#D4AF37' : '#8B4513';
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs bg-[#1c1c1c] border-[#D4AF37]/30 text-foreground shadow-xl">
                      {dim.label}: {dim.value}/100
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>

            {/* Total score */}
            <span className="text-sm font-bold text-muted-foreground">
              {total}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="bg-[#1c1c1c]/95 backdrop-blur-xl border-2 border-[#D4AF37]/30 p-4 min-w-[300px] shadow-2xl"
          sideOffset={8}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-border/50">
              <span className="text-sm font-semibold">PAMSSAC Score</span>
              <span className="text-2xl font-bold text-primary">{total}</span>
            </div>

            {/* All dimensions */}
            <div className="space-y-2">
              {dimensions.map(dim => (
                <div key={dim.key} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{dim.label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getSegmentColor(dim.value)}`}
                        style={{ width: `${dim.value}%` }}
                      />
                    </div>
                    <span className="w-8 text-right font-medium">{dim.value}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Weakest dimensions warning */}
            {weakestTwo && weakestTwo.length > 0 && (
              <div className="pt-3 border-t border-yellow-500/30 bg-yellow-500/10 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  <span className="text-xs font-semibold text-yellow-500">Lowest Dimensions</span>
                </div>
                <div className="space-y-1">
                  {weakestTwo.map((weak, i) => (
                    <div key={i} className="text-xs text-muted-foreground">
                      <span className="font-medium">{String(weak?.dimension || 'Unknown')}:</span> {Number(weak?.score || 0)}/100
                      {weak?.why && <div className="text-xs opacity-70 mt-0.5">{String(weak.why)}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Color legend */}
            <div className="flex items-center gap-3 text-xs pt-2 border-t border-border/50">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-muted-foreground">0-49</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className="text-muted-foreground">50-74</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-muted-foreground">75-100</span>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
